import type { Database } from '@opti-sea/db-types';
import {
  compareRisqCodes,
  RISQ_PREP_TIMINGS,
  RISQ_WORK_TYPES,
  type InspectionPlan,
  type RisqPrepTiming,
  type RisqWorkType,
} from '@opti-sea/risq';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useWorkplace } from '../../core/access';
import { fetchAll, unwrap, useDb, type Db } from '../../core/supabase';
import {
  finalChecksOverview,
  vesselItems,
  type Content,
  type ItemRow,
  type Layers,
  type Question,
  type VesselItem,
} from './model';

type Fn = Database['public']['Functions'];

const workTypeSet = new Set<string>(RISQ_WORK_TYPES);
const timingSet = new Set<string>(RISQ_PREP_TIMINGS);
const asWorkTypes = (values: readonly string[]) =>
  values.filter((v): v is RisqWorkType => workTypeSet.has(v));
const asTimings = (values: readonly string[]) =>
  values.filter((v): v is RisqPrepTiming => timingSet.has(v));

// ---------------------------------------------------------------------------------------------
// Shared content (current RISQ version)
// ---------------------------------------------------------------------------------------------

export async function loadContent(db: Db): Promise<Content | null> {
  const version = unwrap(
    await db.from('risq_versions').select('id, code').eq('is_current', true).is('deleted_at', null),
  )[0];
  if (version === undefined) {
    return null;
  }
  const [sections, questions, positions, positionOrder] = await Promise.all([
    fetchAll((from, to) =>
      db
        .from('risq_sections')
        .select('id, code, sort_order')
        .eq('version_id', version.id)
        .is('deleted_at', null)
        .order('sort_order')
        .range(from, to),
    ),
    fetchAll((from, to) =>
      db
        .from('risq_questions')
        .select(
          'id, key, code, section_id, text_en, guidance_en, advice_en, work_types, prep_timing',
        )
        .eq('version_id', version.id)
        .is('deleted_at', null)
        .order('sort_order')
        .range(from, to),
    ),
    fetchAll((from, to) =>
      db
        .from('risq_question_positions')
        .select('question_id, position_code')
        .is('deleted_at', null)
        .order('id')
        .range(from, to),
    ),
    db.from('positions').select('code, sort_order'),
  ]);
  const rank = new Map(unwrap(positionOrder).map((p) => [p.code, p.sort_order]));
  const sectionCode = new Map(sections.map((s) => [s.id, s.code]));
  const base = new Map<string, string[]>();
  for (const p of positions) {
    base.set(p.question_id, [...(base.get(p.question_id) ?? []), p.position_code]);
  }
  return {
    versionId: version.id,
    versionCode: version.code,
    sections: sections.map((s) => ({ id: s.id, code: s.code, sortOrder: s.sort_order })),
    questions: questions
      .map((q): Question => ({
        id: q.id,
        key: q.key,
        code: q.code,
        sectionId: q.section_id,
        sectionCode: sectionCode.get(q.section_id) ?? '',
        textEn: q.text_en,
        guidanceEn: q.guidance_en,
        adviceEn: q.advice_en,
        workTypes: asWorkTypes(q.work_types),
        prepTiming: asTimings(q.prep_timing),
        basePositions: [...(base.get(q.id) ?? [])].sort(
          (a, b) => (rank.get(a) ?? 999) - (rank.get(b) ?? 999),
        ),
      }))
      .sort((a, b) => compareRisqCodes(a.code, b.code)),
  };
}

export function useContent() {
  const db = useDb();
  return useQuery({ queryKey: ['content'], queryFn: () => loadContent(db), staleTime: 300_000 });
}

// ---------------------------------------------------------------------------------------------
// Company layers
// ---------------------------------------------------------------------------------------------

export async function loadLayers(db: Db, companyId: string): Promise<Layers> {
  const [overrides, flags] = await Promise.all([
    fetchAll((from, to) =>
      db
        .from('risq_position_overrides')
        .select('vessel_id, question_key, position_codes')
        .eq('company_id', companyId)
        .is('deleted_at', null)
        .order('id')
        .range(from, to),
    ),
    fetchAll((from, to) =>
      db
        .from('risq_question_flags')
        .select('question_key, work_types, prep_timing')
        .eq('company_id', companyId)
        .is('deleted_at', null)
        .order('id')
        .range(from, to),
    ),
  ]);
  const companyPositions = new Map<string, string[]>();
  const vesselPositions = new Map<string, Map<string, string[]>>();
  for (const o of overrides) {
    if (o.vessel_id === null) {
      companyPositions.set(o.question_key, o.position_codes);
    } else {
      const map = vesselPositions.get(o.vessel_id) ?? new Map<string, string[]>();
      map.set(o.question_key, o.position_codes);
      vesselPositions.set(o.vessel_id, map);
    }
  }
  return {
    companyPositions,
    vesselPositions,
    flags: new Map(
      flags.map((f) => [
        f.question_key,
        { workTypes: asWorkTypes(f.work_types), prepTiming: asTimings(f.prep_timing) },
      ]),
    ),
  };
}

export function useLayers() {
  const db = useDb();
  const { company } = useWorkplace();
  return useQuery({
    queryKey: ['layers', company.id],
    queryFn: () => loadLayers(db, company.id),
  });
}

// ---------------------------------------------------------------------------------------------
// Vessel data
// ---------------------------------------------------------------------------------------------

export async function loadItemRows(db: Db, vesselId: string): Promise<ItemRow[]> {
  const rows = await fetchAll((from, to) =>
    db
      .from('risq_vessel_items')
      .select(
        'id, question_id, status, status_changed_at, status_changed_by, status_changed_position, last_note, attention_flag, needs_recheck, archived_at',
      )
      .eq('vessel_id', vesselId)
      .is('deleted_at', null)
      .order('id')
      .range(from, to),
  );
  return rows.map((r) => ({
    id: r.id,
    questionId: r.question_id,
    status: r.status,
    statusChangedAt: r.status_changed_at,
    statusChangedBy: r.status_changed_by,
    statusChangedPosition: r.status_changed_position,
    lastNote: r.last_note,
    attentionFlag: r.attention_flag,
    needsRecheck: r.needs_recheck,
    archivedAt: r.archived_at,
  }));
}

const itemsKey = (vesselId: string) => ['items', vesselId] as const;

/** Items of every vessel of the company, with the layers applied. */
export function useFleetItems(): {
  loading: boolean;
  error: Error | null;
  byVessel: Map<string, VesselItem[]>;
  content: Content | null;
} {
  const db = useDb();
  const { vessels } = useWorkplace();
  const content = useContent();
  const layers = useLayers();
  const combine = useCallback(
    (results: { data?: ItemRow[]; isPending: boolean; error: Error | null }[]) => ({
      data: results.map((r) => r.data),
      pending: results.some((r) => r.isPending),
      error: results.find((r) => r.error !== null)?.error ?? null,
    }),
    [],
  );
  const rows = useQueries({
    queries: vessels.map((v) => ({
      queryKey: itemsKey(v.id),
      queryFn: () => loadItemRows(db, v.id),
    })),
    combine,
  });
  const byVessel = useMemo(() => {
    const map = new Map<string, VesselItem[]>();
    vessels.forEach((v, index) => {
      const data = rows.data[index];
      if (data !== undefined && content.data && layers.data) {
        map.set(v.id, vesselItems(content.data, layers.data, v.id, data));
      }
    });
    return map;
  }, [content.data, layers.data, vessels, rows.data]);
  const error = content.error ?? layers.error ?? rows.error;
  return {
    loading: content.isPending || layers.isPending || rows.pending,
    error,
    byVessel,
    content: content.data ?? null,
  };
}

export function useVesselItems(vesselId: string) {
  const db = useDb();
  const content = useContent();
  const layers = useLayers();
  const rows = useQuery({
    queryKey: itemsKey(vesselId),
    queryFn: () => loadItemRows(db, vesselId),
  });
  const items = useMemo(
    () =>
      content.data && layers.data && rows.data
        ? vesselItems(content.data, layers.data, vesselId, rows.data)
        : null,
    [content.data, layers.data, rows.data, vesselId],
  );
  return {
    items,
    content: content.data ?? null,
    layers: layers.data ?? null,
    loading: content.isPending || layers.isPending || rows.isPending,
    error: content.error ?? layers.error ?? rows.error,
  };
}

export function useInspections() {
  const db = useDb();
  const { company } = useWorkplace();
  return useQuery({
    queryKey: ['inspections', company.id],
    queryFn: async () => {
      const rows = unwrap(
        await db
          .from('risq_inspections')
          .select('vessel_id, planned_on, last_voyage_started_on')
          .eq('company_id', company.id)
          .is('deleted_at', null),
      );
      return new Map<string, InspectionPlan>(
        rows.map((r) => [
          r.vessel_id,
          { plannedOn: r.planned_on, lastVoyageStartedOn: r.last_voyage_started_on },
        ]),
      );
    },
  });
}

/**
 * The company's Final checks list and the vessel's marks for the planned inspection date
 * (Task 6b). `null` overview when the company has no list.
 */
export function useFinalChecks(vesselId: string, inspectionOn: string | null) {
  const db = useDb();
  const { company } = useWorkplace();
  return useQuery({
    queryKey: ['finalChecks', company.id, vesselId, inspectionOn],
    enabled: inspectionOn !== null,
    queryFn: async () => {
      const [groups, items, marks] = await Promise.all([
        db
          .from('risq_final_check_groups')
          .select('id, title_en, timing, position_codes, sort_order')
          .eq('company_id', company.id)
          .is('deleted_at', null),
        db
          .from('risq_final_check_items')
          .select('id, group_id, sort_order, text_en, input_kind, input_label_en')
          .eq('company_id', company.id)
          .is('deleted_at', null),
        db
          .from('risq_final_check_marks')
          .select('item_id, checked, value, changed_at, changed_position')
          .eq('vessel_id', vesselId)
          .eq('inspection_on', inspectionOn ?? ''),
      ]);
      const groupRows = unwrap(groups);
      if (groupRows.length === 0) {
        return null;
      }
      return finalChecksOverview(
        groupRows.map((g) => ({
          id: g.id,
          titleEn: g.title_en,
          timing: g.timing,
          positionCodes: g.position_codes,
          sortOrder: g.sort_order,
        })),
        unwrap(items).map((i) => ({
          id: i.id,
          groupId: i.group_id,
          sortOrder: i.sort_order,
          textEn: i.text_en,
          inputKind: i.input_kind,
          inputLabelEn: i.input_label_en,
        })),
        unwrap(marks).map((m) => ({
          itemId: m.item_id,
          checked: m.checked,
          value: m.value,
          changedAt: m.changed_at,
          changedPosition: m.changed_position,
        })),
      );
    },
  });
}

export interface Assignment {
  id: string;
  userId: string;
  positionCode: string;
  startsAt: string;
  revokedAt: string | null;
  revokeReason: string | null;
}

export function useAssignments(vesselIds: readonly string[]) {
  const db = useDb();
  return useQuery({
    queryKey: ['assignments', ...vesselIds],
    queryFn: async () => {
      const rows = unwrap(
        await db
          .from('vessel_assignments')
          .select('id, vessel_id, user_id, position_code, starts_at, revoked_at, revoke_reason')
          .in('vessel_id', [...vesselIds])
          .order('starts_at', { ascending: false }),
      );
      const map = new Map<string, Assignment[]>();
      for (const r of rows) {
        map.set(r.vessel_id, [
          ...(map.get(r.vessel_id) ?? []),
          {
            id: r.id,
            userId: r.user_id,
            positionCode: r.position_code,
            startsAt: r.starts_at,
            revokedAt: r.revoked_at,
            revokeReason: r.revoke_reason,
          },
        ]);
      }
      return map;
    },
    enabled: vesselIds.length > 0,
  });
}

/** Names of everyone in the vessel's history and of the company's office (`crew_names`). */
export function useNames(vesselId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['names', vesselId],
    queryFn: async () =>
      new Map(
        unwrap(await db.rpc('crew_names', { p_vessel_id: vesselId })).map((r) => [
          r.user_id,
          r.full_name,
        ]),
      ),
  });
}

/** Last time the vessel sent anything (newest history entry received by the server). */
export function useLastReceived(vesselIds: readonly string[]) {
  const db = useDb();
  return useQueries({
    queries: vesselIds.map((id) => ({
      queryKey: ['lastReceived', id],
      queryFn: async () =>
        unwrap(
          await db
            .from('risq_item_events')
            .select('received_at')
            .eq('vessel_id', id)
            .order('received_at', { ascending: false })
            .limit(1),
        )[0]?.received_at ?? null,
    })),
  });
}

export function useWeekly(vesselId: string, weeks: readonly string[]) {
  const db = useDb();
  return useQuery({
    queryKey: ['weekly', vesselId, ...weeks],
    queryFn: async () =>
      unwrap(
        await db
          .from('risq_weekly_assignments')
          .select('position_code, week_start, item_id')
          .eq('vessel_id', vesselId)
          .in('week_start', [...weeks])
          .is('deleted_at', null),
      ),
  });
}

export function useRecentEvents(vesselId: string, limit: number) {
  const db = useDb();
  return useQuery({
    queryKey: ['events', vesselId, limit],
    queryFn: async () =>
      unwrap(
        await db
          .from('risq_item_events')
          .select(
            'id, item_id, actor_id, actor_position, type, from_status, to_status, text, superseded, client_created_at',
          )
          .eq('vessel_id', vesselId)
          .order('client_created_at', { ascending: false })
          .limit(limit),
      ),
  });
}

export function useAccessHistory(vesselId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['audit', vesselId],
    queryFn: async () =>
      unwrap(
        await db
          .from('audit_log')
          .select('id, actor_id, action, details, created_at')
          .eq('vessel_id', vesselId)
          .order('created_at', { ascending: false })
          .limit(50),
      ),
  });
}

// ---------------------------------------------------------------------------------------------
// One question on one vessel
// ---------------------------------------------------------------------------------------------

export function useItemDetail(
  itemId: string,
  questionId: string | null,
  questionKey: string | null,
) {
  const db = useDb();
  const { company } = useWorkplace();
  return useQuery({
    queryKey: ['item', itemId],
    enabled: questionId !== null && questionKey !== null,
    queryFn: async () => {
      const [events, evidence, checks, points, ownPoints, notes, layerChanges] = await Promise.all([
        db
          .from('risq_item_events')
          .select(
            'id, actor_id, actor_position, type, from_status, to_status, text, checked, wtc_item_id, company_wtc_item_id, superseded, client_created_at',
          )
          .eq('item_id', itemId)
          .order('client_created_at', { ascending: false }),
        db
          .from('risq_evidence')
          .select('id, taken_at, tz_offset_min, taken_by, taken_position, storage_path, thumb_path')
          .eq('item_id', itemId)
          .is('deleted_at', null)
          .order('taken_at', { ascending: false }),
        db
          .from('risq_vessel_wtc_checks')
          .select('wtc_item_id, company_wtc_item_id, checked')
          .eq('item_id', itemId),
        db
          .from('risq_wtc_items')
          .select('id, item_key, sort_order, text_en')
          .eq('question_id', questionId ?? '')
          .is('deleted_at', null),
        db
          .from('risq_company_wtc_items')
          .select('id, item_key, is_own, text_en, hidden, sort_order')
          .eq('company_id', company.id)
          .eq('question_key', questionKey ?? '')
          .is('deleted_at', null),
        db
          .from('risq_company_notes')
          .select('id, kind, text, origin, sort_order')
          .eq('company_id', company.id)
          .eq('question_key', questionKey ?? '')
          .is('deleted_at', null)
          .order('sort_order'),
        db
          .from('risq_layer_changes')
          .select(
            'id, vessel_id, layer, target, action, item_key, old_value, new_value, actor_id, actor_role, created_at',
          )
          .eq('company_id', company.id)
          .eq('question_key', questionKey ?? '')
          .order('created_at', { ascending: false }),
      ]);
      return {
        events: unwrap(events),
        evidence: unwrap(evidence),
        checks: unwrap(checks),
        points: unwrap(points),
        ownPoints: unwrap(ownPoints),
        notes: unwrap(notes),
        layerChanges: unwrap(layerChanges),
      };
    },
  });
}

/** Short-lived links to evidence files in the private bucket (PLAN §2.8). */
export function useSignedUrls(paths: readonly string[]) {
  const db = useDb();
  return useQuery({
    queryKey: ['signed', ...paths],
    enabled: paths.length > 0,
    staleTime: 240_000,
    queryFn: async () => {
      const { data } = await db.storage.from('evidence').createSignedUrls([...paths], 300);
      return new Map(
        (data ?? []).map((d) => [d.path ?? '', d.error === null ? d.signedUrl : null]),
      );
    },
  });
}

// ---------------------------------------------------------------------------------------------
// Changes (server functions only, D39)
// ---------------------------------------------------------------------------------------------

type RpcName = keyof Fn;

/**
 * Arguments of a server function. The generated types do not show which arguments accept null
 * (e.g. `p_vessel_id` null = company layer); the functions check their arguments themselves.
 */
export type RpcArgs<N extends RpcName> = {
  [K in keyof Fn[N]['Args']]: Fn[N]['Args'][K] | null;
};

/** Calls a server function and refreshes the given queries afterwards. */
export function useRpc<N extends RpcName>(name: N, invalidate: readonly (readonly unknown[])[]) {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (args: RpcArgs<N>) => {
      // supabase-js cannot narrow the argument type of a generic function name.
      const result = await db.rpc(name, args as never);
      if (result.error !== null) {
        throw new Error(result.error.message);
      }
      return result.data as Fn[N]['Returns'];
    },
    onSuccess: async () => {
      await Promise.all(invalidate.map((key) => client.invalidateQueries({ queryKey: key })));
    },
  });
}
